import React, { lazy, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'formik';
import ReactDatePicker from 'react-datepicker';

import { UI_DATE_LOWER_FORMAT } from 'globalconfig/constants/static';
import { pageUrls } from 'globalconfig/constants/keys';
import { getDateLocale } from 'globalutils/fmUtils';

// Dynamic layout-based imports
const layout = window?.$environment?.CURRENT_LAYOUT;
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const Upload = lazy(() => import(`globalcomponents/FileUpload/${layout}`));
const SubHeader = lazy(() => import(`globalcomponents/SubHeader/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`);

const RenderComponent = ({
  values,
  handleEdit,
  errors,
  isNotValid,
  validateForm,
  customProps,
  submitAction,
  onClear,
}) => {
  const { history, i18n, hasApiStatus, drivers, fleetVehicleList, removeValues } = customProps;
  const dateFormat = UI_DATE_LOWER_FORMAT;

  const fields = {
    driverId: { type: 'select', field: 'driverId', label: i18n.t('common.driverName'), placeholder: i18n.t('common.pleaseSelect') },
    date: { type: 'date', field: 'date', label: i18n.t('common.date'), placeholder: dateFormat },
    vehicleName: { type: 'text', field: 'vehicleName', label: i18n.t('common.vehicleName'), placeholder: i18n.t('common.vehicleName') },
    logData: { type: 'file', field: 'logData', label: i18n.t('common.upload'), placeholder: i18n.t('common.upload') },
  };

  const todayMinusOneDay = new Date(new Date().setDate(new Date().getDate() - 1));
  const addToMonth = (months = 0) => {
    const d = new Date();
    d.setMonth(d.getMonth() - months);
    return d;
  };

  useEffect(() => {
    if (removeValues === 'true') {
      onClear();
    }
  }, [removeValues]);

  const handleSubmit = () => {
    validateForm().then((data) => submitAction(data));
  };

  const handleCancel = () => {
    onClear();
    history.push(pageUrls.logsList);
  };

  return (
    <>
      {hasApiStatus?.errorMessage && (
        <Message type="error" message={i18n.t(hasApiStatus.errorMessage)} />
      )}

      <SubHeader text={i18n.t('common.userInformation')} className="addLogTitle" />

      {/* Driver Name */}
      <FormStyle.default.FieldsWrapper className="formWrapper">
        <Label htmlFor="driverId">{fields.driverId.label}</Label>
        <FormStyle.default.TextWrapper>
          <Field
            id="driverId"
            name={fields.driverId.field}
            as={Select}
            placeholder={fields.driverId.placeholder}
            value={values.driverId || ''}
            type={fields.driverId.label}
            suggestions={drivers.data}
            onChange={(e) => handleEdit(e, fields.driverId)}
            isClearable
          />
          {errors.driverId && !isNotValid && <Message type="error" message={errors.driverId} />}
        </FormStyle.default.TextWrapper>
      </FormStyle.default.FieldsWrapper>

      {/* Vehicle Name */}
      <FormStyle.default.FieldsWrapper className="formWrapper">
        <Label htmlFor="vehicleName">{fields.vehicleName.label}</Label>
        <FormStyle.default.TextWrapper>
          <Field
            id="vehicleName"
            name={fields.vehicleName.field}
            as={Select}
            placeholder={fields.vehicleName.placeholder}
            value={values.vehicleName}
            type={fields.vehicleName.label}
            suggestions={fleetVehicleList}
            onChange={(e) => handleEdit(e, fields.vehicleName)}
            isClearable
          />
          {errors.vehicleName && !isNotValid && <Message type="error" message={errors.vehicleName} />}
        </FormStyle.default.TextWrapper>
      </FormStyle.default.FieldsWrapper>

      {/* Date */}
      <FormStyle.default.FieldsWrapper className="formWrapper">
        <Label htmlFor="date">{fields.date.label}</Label>
        <FormStyle.default.TextWrapper>
          <Field
            id="date"
            name={fields.date.field}
            as={ReactDatePicker}
            placeholderText={fields.date.placeholder}
            value={values.date}
            type={fields.date.label}
            locale={getDateLocale()}
            showPopperArrow={false}
            showTimeSelect={fields.date.isTimeNeeded}
            maxDate={todayMinusOneDay}
            minDate={addToMonth(1)}
            showDisabledMonthNavigation
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            onChange={(e) => handleEdit(e, fields.date)}
          />
          {errors.date && !isNotValid && <Message type="error" message={errors.date} />}
        </FormStyle.default.TextWrapper>
      </FormStyle.default.FieldsWrapper>

      {/* Upload Log */}
      <FormStyle.default.FieldsWrapper className="formWrapper">
        <Label htmlFor="logData">{fields.logData.label}</Label>
        <FormStyle.default.TextWrapper>
          <Field
            id="logData"
            name={fields.logData.field}
            as={Upload}
            normal
            allowMultiple={false}
            totalFiles={1}
            placeholderText={fields.logData.placeholder}
            value={values.logData || ''}
            type={fields.logData.label}
            acceptedFileTypes={['application/pdf']}
            fileValidateTypeLabelExpectedTypesMap={{ 'application/pdf': '.pdf' }}
            labelFileTypeNotAllowed={'Only PDF file is allowed.'}
            allowFileTypeValidation
            removeValues={removeValues}
            onUpload={(e) => handleEdit(e, { field: 'logData', type: 'file' })}
            fileChosen={(e) => {
              if (e && e.length === 0) {
                handleEdit(e, { field: 'logData', type: 'file' });
              }
            }}
          />
          {errors.logData && !isNotValid && <Message type="error" message={errors.logData} />}
        </FormStyle.default.TextWrapper>
      </FormStyle.default.FieldsWrapper>

      {/* Buttons */}
      <FormStyle.default.ButtonsWrapper>
        <Button className="buttonStyle" type="reset" label={i18n.t('common.cancel')} onClick={handleCancel} />
        <Button className="buttonStyle" type="submit" label={i18n.t('common.save')} onClick={handleSubmit} />
      </FormStyle.default.ButtonsWrapper>
    </>
  );
};

RenderComponent.propTypes = {
  values: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  isNotValid: PropTypes.bool.isRequired,
  customProps: PropTypes.object.isRequired,
  validateForm: PropTypes.func.isRequired,
  submitAction: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export default RenderComponent;
